import Add from '@mui/icons-material/Add'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Box,
  Button,
  IconButton,
  Link,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import startCase from 'lodash/startCase'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { GameType } from '../../../generated/graphql'
import useCurrentGameRoute from '../../../hooks/useCurrentGameRoute'
import useDashboardFilters from '../../../hooks/useDashboardFilters'
import useGames from '../../../hooks/useGames/useGames'
import useLayout from '../../../hooks/useLayout'
import Arrow from '../../../icons/ArrowRight'
import MenuIcon from '../../../icons/Menu'
import { useTheme } from '../../../theme'
import getGameStatusLabelColor from '../../../utils/getGameStatusLabelColor'
import FavouriteButton from '../../FavouriteButton'
import GameAvatar from '../../GameAvatar'
import Logo from '../../Logo'
import AddGameSearch from './AddGameSearch'

const NavbarMenu = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [hoveredGameId, setHoveredGameId] = useState<string | null>(null)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { game, slug } = useCurrentGameRoute()
  const theme = useTheme()
  const { setIsMobileSidebarOpen } = useLayout()
  const navigate = useNavigate()
  const matches = useMediaQuery(theme.breakpoints.down('sm'))
  const allGames = useGames()
  const { isFavourite, toggleFavourite } = useDashboardFilters()

  const sortAlphabetically = (a: any, b: any) => {
    return a.info.name.localeCompare(b.info.name)
  }

  // Show only favorited games, sorted alphabetically
  const games =
    allGames?.filter((game) => isFavourite(game.id)).sort(sortAlphabetically) ||
    []

  const handleToggleFavourite = (gameId: string) => {
    toggleFavourite(gameId)
    setHoveredGameId(null)
  }

  const headerNavPadding = matches ? 16 : 24

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        event.target &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setIsSearchOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setIsSearchOpen(false)
    }
  }, [isOpen])

  return (
    <Box
      sx={{
        width: 280 - headerNavPadding,
        borderRight: `1px solid ${theme.colors.headerDividers}`,
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <IconButton
        color='inherit'
        onClick={() => setIsMobileSidebarOpen((prevState) => !prevState)}
        sx={{
          width: '45px',
          height: '45px',
          display: { lg: 'none' },
        }}
      >
        <MenuIcon fontSize='small' />
      </IconButton>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <RouterLink to={'/'}>
          <Logo
            sx={{
              height: 45,
              width: 45,
            }}
            color={theme.colors.header}
          />
        </RouterLink>
      </Box>

      <Button
        variant={'text'}
        sx={{
          width: '100%',
          height: '100%',
          py: 0,
          px: 2,
          color: theme.palette.primary.contrastText,
          justifyContent: 'space-between',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
        onClick={() => setIsOpen((prevState) => !prevState)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left',
            width: `calc(100% - 24px)`,
            pr: 1,
          }}
        >
          <Typography
            variant={'body2'}
            textAlign={'left'}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {slug ? game?.info.name : t('navigation.allGames')}
          </Typography>
          {slug ? (
            <Typography
              variant={'caption'}
              sx={{
                display: 'block',
                color:
                  game?.info.type === GameType.Compact ? '#FFF579' : '#FFFFFF',
              }}
            >
              {startCase(game?.info.type)}
            </Typography>
          ) : null}
        </Box>

        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </Button>
      {isOpen && (
        <Box
          ref={dropdownRef}
          sx={{
            position: 'absolute',
            top: '64px',
            backgroundColor: theme.colors.header,
            width: 280,
            left: -headerNavPadding,
            borderTop: `1px solid ${theme.colors.headerDividers}`,
          }}
        >
          <List>
            {games?.map((game) => (
              <ListItemButton
                key={game.id}
                sx={{
                  px: 3,
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: theme.colors.darkBlue,
                  },
                }}
                onClick={() => {
                  navigate(`/games/${game.info.slug}`)
                  setIsOpen(false)
                  setIsSearchOpen(false)
                }}
                onMouseEnter={() => setHoveredGameId(game.id)}
                onMouseLeave={() => setHoveredGameId(null)}
              >
                <ListItemAvatar>
                  <GameAvatar
                    gameId={game.id}
                    sx={{
                      border: `3px solid ${getGameStatusLabelColor(game.info.status)}`,
                    }}
                  />
                </ListItemAvatar>
                <ListItemText primary={game.info.name} />

                {/* Remove button for removing from favourites - Only visible on hover */}
                <Box
                  sx={{
                    position: 'absolute',
                    right: 8,
                    opacity: hoveredGameId === game.id ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out',
                  }}
                >
                  <FavouriteButton
                    isFavourite={isFavourite(game.id)}
                    onToggle={() => handleToggleFavourite(game.id)}
                    size='small'
                    variant='remove'
                  />
                </Box>
              </ListItemButton>
            ))}

            {/* Add Game Button */}
            <Box
              sx={{
                px: 3,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Tooltip
                title={t('common.addGameToFavourites')}
                placement='right'
                open={tooltipOpen && !isSearchOpen}
                onOpen={() => setTooltipOpen(true)}
                onClose={() => setTooltipOpen(false)}
              >
                <IconButton
                  onClick={() => {
                    setTooltipOpen(false)
                    setIsSearchOpen(!isSearchOpen)
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    border: `2px dashed ${theme.palette.primary.contrastText}`,
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Add fontSize='small' />
                </IconButton>
              </Tooltip>

              {/* Inline Search Component */}
              <Box sx={{ flex: 1 }}>
                <AddGameSearch
                  isOpen={isSearchOpen}
                  onClose={() => setIsSearchOpen(false)}
                />
              </Box>
            </Box>
          </List>

          <Link
            color={theme.palette.primary.contrastText}
            component={RouterLink}
            to={'/games/'}
            variant={'inherit'}
            underline={'none'}
            onClick={() => {
              setIsOpen(false)
              setIsSearchOpen(false)
            }}
            py={1}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              borderTop: `1px solid ${theme.colors.headerDividers}`,
              px: 3,
              py: 2,
            }}
          >
            <Typography pr={1} variant={'subtitle2'}>
              {t('navigation.seeAllGames')}
            </Typography>
            <Arrow sx={{ fontSize: 16 }} />
          </Link>
        </Box>
      )}
    </Box>
  )
}

export default NavbarMenu
